<template>
  <o-modal v-if="visible" @close="closeModal" width="850px" modalClass="upsell-rich-modal" headerClass="border-0 pb-0" footerClass="pt-4 pb-4">
    <template v-slot:header>
      <span></span>
    </template>
    
    <template v-slot:body>
      <div class="upsell-modal-body">
        
        <!-- Header part inside body for centering -->
        <div class="upsell-header-centered">
          <div class="upsell-icon-wrapper">
            <i :class="content.icon || 'fas fa-crown'"></i>
          </div>
          <h3 class="upsell-title">Unlock {{ content.title || 'Feature' }}</h3>
          <p class="upsell-tagline">{{ content.tagline || 'Get full control and maximize your module experience.' }}</p>
        </div>
        
        <!-- Content row -->
        <div class="upsell-content-row mt-2">
          <!-- Left side: Graphic -->
          <div class="upsell-graphic">
            <div class="graphic-placeholder">
              <UpsellGraphic class="upsell-svg-inline" />
            </div>
          </div>
          
          <!-- Right side: Benefits -->
          <div class="upsell-benefits-list">
            <div v-for="(benefit, index) in (content.benefits || defaultBenefits)" :key="index" class="benefit-item">
              <div class="benefit-icon-wrapper">
                <i class="fas fa-check-circle benefit-icon"></i>
              </div>
              <div class="benefit-text">
                <h4 class="benefit-title">{{ benefit.title }}</h4>
                <p class="benefit-desc">{{ benefit.desc }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="content.depends_on_feature" class="upsell-note">
          <i class="fas fa-info-circle me-1"></i>
          Note: This feature requires the <strong>{{ formatFeatureName(content.depends_on_feature) }}</strong> module to be active.
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <div class="upsell-actions">
        <!-- Maybe Later -->
        <button type="button" class="btn btn-maybe-later" @click="closeModal">
          Maybe Later
        </button>

        <!-- Unlock Now (CTA) -->
        <button
          type="button"
          class="btn btn-unlock-now"
          @click="handleCtaClick"
        >
          <i class="fas fa-envelope me-2"></i> {{ content.cta_label || 'Contact Sales to Unlock' }}
        </button>
        
      </div>
    </template>
  </o-modal>
</template>

<script>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import OModal from '@/components/OmnihControl/OModal.vue';
import UpsellGraphic from './UpsellGraphic.vue';
import { buildUnlockRequestMailto } from '@/helpers/mailtoBuilder';
import { useFeatureUpsell } from '@/composables/useFeatureUpsell';

export default {
  name: 'FeatureUpsellModal',
  components: {
    OModal,
    UpsellGraphic
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    featureKey: {
      type: String,
      required: true
    },
    context: {
      type: String,
      default: 'page_direct_access'
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore();
    const { getContent, log, dismiss } = useFeatureUpsell();
    
    const content = computed(() => getContent(props.featureKey) || {});
    

    const corporate = computed(() => store.getters['Corporate/corporate'] || store.getters['Auth/user']?.corporates?.[0]);

    // Fallback benefits if none provided
    const defaultBenefits = [
      { title: 'Advanced Manipulation', desc: 'Adjust points manually, bulk update, and more.' },
      { title: 'Expiration Control', desc: 'Set custom expiration rules and notifications.' },
      { title: 'Detailed Reports', desc: 'Analyze point transactions and member activity.' }
    ];

    const handleCtaClick = () => {
      log(props.featureKey, 'cta_clicked', props.context);
      
      const user = store.getters['Auth/user'] || store.state.Auth?.currUser;
      
      // Load corporate name from localStorage mimicking navbar-desktop.vue behavior
      const selectedProfileStr = window.localStorage?.getItem('selected_business_profile');
      let localCorpName = null;
      if (selectedProfileStr) {
        try {
          const profile = JSON.parse(selectedProfileStr);
          localCorpName = profile.corporate_name;
        } catch (e) {
          // ignore parsing error
        }
      }

      const corpName = localCorpName || corporate.value?.name || corporate.value?.corporate_name || user?.corporate_name || 'Corporate Account';

      const mailtoLink = buildUnlockRequestMailto({
        corporateName: corpName,
        moduleName: content.value.title || props.featureKey,
        benefits: content.value.benefits || defaultBenefits,
        requester: user
      });
      window.location.href = mailtoLink;
      
      closeModal();
    };

    const closeModal = () => {
      log(props.featureKey, 'dismissed', props.context);
      dismiss(props.featureKey);
      emit('close');
    };

    watch(() => props.visible, (newVal) => {
      if (newVal) {
        log(props.featureKey, 'impression', props.context);
      }
    });

    const formatFeatureName = (key) => {
      if (!key) return '';
      const featureTitleMap = {
        'point_control': 'Point Control',
        'marketing_module': 'Marketing Module',
        'transaction_report_module': 'Transaction Report Module',
        'threshold_setting': 'Threshold Setting',
        'newsletter_module': 'Newsletter Module',
        'promotions_module': 'Promotions Module',
        'voucher_gift': 'Voucher Gift',
      };
      if (featureTitleMap[key]) {
        return featureTitleMap[key];
      }
      return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    return {
      content,
      defaultBenefits,
      handleCtaClick,
      closeModal,
      formatFeatureName
    };
  }
}
</script>

<style>
.omni-modal-container.upsell-rich-modal {
  max-width: 850px !important;
  width: 100%;
}
</style>

<style scoped>
.upsell-modal-body {
  padding: 0 20px;
}
.upsell-header-centered {
  text-align: center;
  margin-bottom: 30px;
}
.upsell-icon-wrapper {
  width: 64px;
  height: 64px;
  background-color: color-mix(in srgb, var(--primary-color, #10b981) 10%, transparent);
  color: var(--primary-color, #10b981);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 16px auto;
}
.upsell-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}
.upsell-tagline {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
}
.upsell-content-row {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  margin-bottom: 20px;
}
.upsell-graphic {
  flex: 1;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.graphic-placeholder {
  width: 100%;
  aspect-ratio: 4/3;
  background-color: #f1f5f9;
  border-radius: 12px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}
.upsell-svg-inline {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.upsell-benefits-list {
  flex: 1.2;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 24px;
}
.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.benefit-icon-wrapper {
  width: 40px;
  height: 40px;
  background-color: color-mix(in srgb, var(--primary-color, #10b981) 10%, transparent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.benefit-icon {
  color: var(--primary-color, #10b981);
  font-size: 1.25rem;
}
.benefit-title {
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  font-size: 0.95rem;
}
.benefit-desc {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}
.upsell-note {
  padding: 12px 16px;
  background-color: color-mix(in srgb, var(--secondary-color, #f59e0b), #ffffff);
  border: 1px solid color-mix(in srgb, var(--secondary-color, #f59e0b), transparent);
  border-radius: 8px;
  color: var(--primary-color, #10b981);
  font-size: 0.85rem;
  text-align: left;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.upsell-note i {
  color: var(--primary-color, #10b981);
  font-size: 1rem;
  flex-shrink: 0;
}
.upsell-note strong {
  color: var(--primary-color, #10b981);
  font-weight: 700;
}
.upsell-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}
.btn-maybe-later {
  background-color: #f1f5f9;
  color: #64748b;
  font-weight: 600;
  padding: 10px 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}
.btn-maybe-later:hover {
  background-color: #e2e8f0;
  color: #475569;
}
.btn-unlock-now {
  background-color: var(--primary-color, #10b981);
  color: white;
  font-weight: 600;
  padding: 10px 32px;
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}
.btn-unlock-now:hover {
  filter: brightness(0.9);
  color: white;
}
.btn-connect-sales {
  background-color: #334155; /* dark slate */
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  padding: 8px 24px;
  border-radius: 6px;
  border: none;
}
.btn-connect-sales:hover {
  background-color: #1e293b;
  color: white;
}
</style>
